import type { RetailPriceFieldProps } from '../types';
import { EditedTaxFieldInputContainer } from './EditedTaxField.style';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { getTaxFieldName, useEditableProp } from './helpers';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useDispatch, useSelector } from 'react-redux';
import { convertToNumber } from '../../../utils/formatUtils';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { useEffect, useRef } from 'react';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { TAXES_NUMERIC_MAX_VALUE_MESSAGE } from '../constants';

export default function TaxItemAmountField<Props extends RetailPriceFieldProps>({ data, handleBlur }: Props) {
    const formattedAmount = formatNumberAndCents(data.amount);
    const [amount, setAmount, isEdited] = useEditableProp(formattedAmount, data.isEditedAmount);
    const fieldName = getTaxFieldName(data.name, false);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isTaxAmountValid = useSelector(dealScreenErrorSelectors.getFieldValidationError(DealTabs.TAXES, fieldName));
    const isLocked = dealLockHooks.useIsDealLocked();
    const dispatch = useDispatch();
    const prevValueBeforeBlur = useRef<string>(amount || '');
    const isBlurInProgress = useRef(false);
    const isTaxAmountValidRef = useRef(isTaxAmountValid);

    const setValidAmount = () => {
        if (!amount || formattedAmount === '$--' || !formattedAmount) {
            setAmount('0.00');
            prevValueBeforeBlur.current = '0.00';
        }
    };

    useEffect(() => {
        isTaxAmountValidRef.current = isTaxAmountValid;
    }, [isTaxAmountValid]);

    useEffect(() => {
        return () => {
            if (isTaxAmountValidRef.current) {
                dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, false));
            }
        };
    }, []);

    const handleBlurWrapper = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        isBlurInProgress.current = true;
        prevValueBeforeBlur.current = e.target.value;
        setValidAmount();
        handleBlur(e, data.amountKey);
    };

    const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            setValidAmount();
            const formatedInput = convertToNumber(e.currentTarget.value);
            setAmount(formatNumberAndCents(formatedInput));
            handleBlur({ target: { value: formatedInput.toString() } }, data.amountKey);
            prevValueBeforeBlur.current = formatedInput.toString();
        }
    };

    useEffect(() => {
        if (data.isDisabled && isTaxAmountValidRef.current) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, false));
        }
    }, [data.isDisabled]);

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        if (isBlurInProgress.current) {
            isBlurInProgress.current = false;
            return;
        }
        dispatch(
            offerActionCreators.updatePaymentCallPendingState(
                convertToNumber(e.target.value) !== convertToNumber(prevValueBeforeBlur.current)
            )
        );

        const hasErrorInAmountField = !e.isValid;
        if (isDealScreenRedDotErrorsEnabled && isTaxAmountValid !== hasErrorInAmountField)
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.TAXES, fieldName, hasErrorInAmountField));

        setAmount(e.target.value);
    };

    return (
        <EditedTaxFieldInputContainer $isEdited={isEdited}>
            <NumericInput
                data-testid={`detailed-table-cell-amount-input-${data.name}`}
                name={'detail-amount'}
                inputPrefix={'$'}
                onChange={handleChange}
                value={amount}
                sx={{ width: '9rem' }}
                onBlur={handleBlurWrapper}
                onKeyDown={handleEnterKey}
                allowDecimal
                decimalMaxLength={2}
                maxValue={999999.99}
                placeholder="0.00"
                autoInsertCommas={true}
                textAlign="right"
                disabled={isLocked || data.isDisabled || false}
                size="sm"
                customErrorMessages={{
                    exceedsMaxValueRule: TAXES_NUMERIC_MAX_VALUE_MESSAGE
                }}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            />
        </EditedTaxFieldInputContainer>
    );
}
